import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Form,
  Button,
  Card,
  Row,
  Col,
  Container,
  Table,
  Badge,
  Modal,
} from "react-bootstrap";

import {
  Phone,
  TrendingUp,
  PieChart,
  Trash2,
  PlusCircle,
  RefreshCw,
  Send,
  User,
} from "react-feather";
import { FaWallet as Wallet } from "react-icons/fa";
import Swal from "sweetalert2";

import PPIDmtService from "../../../services/PPIDmtService";
import SideBar from "../../../components/Sidebar";
import LoadingScreen from "../../../components/LoadingScreen/LoadingScreen";

const SenderHome = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { customerMobile, data } = location.state || {};

  const [loading, setLoading] = useState(false);
  const [beneficiaryList, setBeneficiaryList] = useState([]);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [selectedBeneficiary, setSelectedBeneficiary] = useState(null);
  const [deleteToken, setDeleteToken] = useState(null);
  const [otp, setOtp] = useState("");
  const [message, setMessage] = useState(null);

  // Fetch beneficiary list on component mount
  useEffect(() => {
    if (customerMobile && data?.token) {
      fetchBeneList();
    }
  }, [customerMobile, data?.token]);

  const fetchBeneList = async () => {
    if (loading) return;

    setLoading(true);

    try {
      const result = await PPIDmtService.beneficiaryList(
        data?.token,
        customerMobile
      );

      if (!result.success) {
        Swal.fire("Oops!", result.message || "Request failed!", "error");
        return;
      }

      const res = result.data;

      if (res.status === "SUCCESS") {
        setBeneficiaryList(res?.data?.beneficiaries || []);
      } else {
        Swal.fire("Oops!", res.message || "Something went wrong", "error");
      }
    } catch (error) {
      Swal.fire(
        "Oops!",
        error.response?.data?.message || error.message || "Network error",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const deleteBeneHandler = async (beneficiary) => {
    if (loading) return;

    setSelectedBeneficiary(beneficiary);
    setLoading(true);

    try {
      const result = await PPIDmtService.deleteBeneficiary(
        data?.token,
        customerMobile,
        beneficiary.beneficiary_id
      );

      if (!result.success) {
        Swal.fire("Oops!", result.message || "Request failed!", "error");
        return;
      }

      const res = result.data;

      if (res.status === "SUCCESS") {
        setMessage(res?.data?.message);
        setDeleteToken(res?.data?.token);
        setShowOtpModal(true);
        //   Swal.fire("OTP Sent!", "OTP has been sent for verification", "info");
      } else {
        Swal.fire("Oops!", res.message || "Something went wrong", "error");
      }
    } catch (error) {
      Swal.fire(
        "Oops!",
        error.response?.data?.message || error.message || "Network error",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const verifyDeleteBeneHandler = async () => {
    if (loading || !otp.trim()) {
      Swal.fire("Warning!", "Please enter OTP", "warning");
      return;
    }

    setLoading(true);

    try {
      const result = await PPIDmtService.validateDeleteBeneficiary(
        data?.token,
        customerMobile,
        deleteToken,
        otp
      );

      if (!result.success) {
        Swal.fire("Oops!", result.message || "Request failed!", "error");
        return;
      }

      const res = result.data;

      if (res.status === "SUCCESS") {
        setShowOtpModal(false);
        setOtp("");
        setDeleteToken(null);
        setSelectedBeneficiary(null);
        await fetchBeneList();
        Swal.fire("Success!", "Beneficiary deleted successfully", "success");
      } else {
        Swal.fire("Oops!", res.message || "Something went wrong", "error");
      }
    } catch (error) {
      Swal.fire(
        "Oops!",
        error.response?.data?.message || error.message || "Network error",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleAddBeneficiary = () => {
    navigate("/ppi-money-transfer/add-beneficiary", {
      state: { customerMobile, data },
    });
  };

  const handleFundTransfer = (beneficiary) => {
    navigate("/ppi-money-transfer/fund-transfer", {
      state: {
        customerMobile,
        data,
        beneficiary,
      },
    });
  };

  const handleRefresh = () => {
    fetchBeneList();
  };

  return (
    <Container fluid className="mt-3">
      {loading && <LoadingScreen />}

      <Row>
        <SideBar />

        <Col md={9}>
          {/* Sender Information Card */}
          <Card className="shadow-sm p-4 mb-4" style={{ borderRadius: "12px" }}>
            <h4 className="mb-4 text-primary">Sender Dashboard</h4>

            {/* Sender Information - Compact Horizontal Layout */}
            <Row className="mb-3">
              <Col md={8}>
                <div className="d-flex flex-column flex-md-row gap-3">
                  {/* Wallet Holder Card */}
                  <div
                    className="bg-light rounded p-3 d-flex align-items-center"
                    style={{ flex: 1 }}
                  >
                    <div className="bg-primary rounded-circle p-2 me-3">
                      <User size={20} className="text-white" />
                    </div>
                    <div>
                      <small className="text-muted d-block">
                        Wallet Holder
                      </small>
                      <strong className="text-dark">
                        {data?.wallet_holder_name || "N/A"}
                      </strong>
                    </div>
                  </div>

                  {/* Mobile Card */}
                  <div
                    className="bg-light rounded p-3 d-flex align-items-center"
                    style={{ flex: 1 }}
                  >
                    <div className="bg-info rounded-circle p-2 me-3">
                      <Phone size={20} className="text-white" />
                    </div>
                    <div>
                      <small className="text-muted d-block">
                        Customer Mobile Number
                      </small>
                      <strong className="text-dark">
                        {customerMobile || "N/A"}
                      </strong>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>

            {/* Limits Section - More Compact Design */}
            <div className="mb-1">
              <h6 className="text-muted mb-3">Wallet Limits</h6>
              <Row>
                <Col md={4} className="mb-3">
                  <div className="border-start border-3 border-success p-3 bg-light rounded-end">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <small className="text-muted d-block">
                          Limit Consumed
                        </small>
                        <h5 className="text-success mb-0">
                          ₹{data?.wallet_to_bank_limit_consumed || 0}
                        </h5>
                      </div>
                      <div className="bg-success rounded-circle p-2">
                        <TrendingUp size={18} className="text-white" />
                      </div>
                    </div>
                  </div>
                </Col>

                <Col md={4} className="mb-3">
                  <div className="border-start border-3 border-primary p-3 bg-light rounded-end">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <small className="text-muted d-block">
                          Available Limit
                        </small>
                        <h5 className="text-primary mb-0">
                          ₹{data?.wallet_to_bank_limit_available || 0}
                        </h5>
                      </div>
                      <div className="bg-primary rounded-circle p-2">
                        <Wallet size={18} className="text-white" />
                      </div>
                    </div>
                  </div>
                </Col>

                <Col md={4} className="mb-3">
                  <div className="border-start border-3 border-info p-3 bg-light rounded-end">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <small className="text-muted d-block">
                          Total Limit
                        </small>
                        <h5 className="text-info mb-0">
                          ₹
                          {(
                            parseFloat(
                              data?.wallet_to_bank_limit_consumed || 0
                            ) +
                            parseFloat(
                              data?.wallet_to_bank_limit_available || 0
                            )
                          ).toFixed(2)}
                        </h5>
                      </div>
                      <div className="bg-info rounded-circle p-2">
                        <PieChart size={18} className="text-white" />
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>

            {/* Progress Bar for Visual Representation */}
            <div className="">
              <div className="d-flex justify-content-between mb-2">
                <small className="text-muted">Limit Usage</small>
                <small className="text-muted">
                  {(
                    (parseFloat(data?.wallet_to_bank_limit_consumed || 0) /
                      (parseFloat(data?.wallet_to_bank_limit_consumed || 0) +
                        parseFloat(
                          data?.wallet_to_bank_limit_available || 0
                        ))) *
                    100
                  ).toFixed(1)}
                  %
                </small>
              </div>
              <div className="progress" style={{ height: "8px" }}>
                <div
                  className="progress-bar bg-warning"
                  role="progressbar"
                  style={{
                    width: `${
                      (parseFloat(data?.wallet_to_bank_limit_consumed || 0) /
                        (parseFloat(data?.wallet_to_bank_limit_consumed || 0) +
                          parseFloat(
                            data?.wallet_to_bank_limit_available || 0
                          ))) *
                      100
                    }%`,
                  }}
                  aria-valuenow={
                    (parseFloat(data?.wallet_to_bank_limit_consumed || 0) /
                      (parseFloat(data?.wallet_to_bank_limit_consumed || 0) +
                        parseFloat(
                          data?.wallet_to_bank_limit_available || 0
                        ))) *
                    100
                  }
                  aria-valuemin="0"
                  aria-valuemax="100"
                ></div>
              </div>
            </div>
          </Card>
          {/* Beneficiary List Card */}
          <Card className="shadow-sm p-3 mb-3" style={{ borderRadius: "12px" }}>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h5 className="mb-0">Beneficiaries</h5>
              <div>
                <Button
                  variant="outline-primary"
                  className="me-2"
                  onClick={handleRefresh}
                  disabled={loading}
                >
                  <RefreshCw size={18} /> Refresh
                </Button>
                <Button
                  variant="success"
                  onClick={handleAddBeneficiary}
                  disabled={loading}
                >
                  <PlusCircle size={18} /> Add Beneficiary
                </Button>
              </div>
            </div>

            {beneficiaryList.length === 0 ? (
              <div className="text-center py-5">
                <p className="text-muted">No beneficiaries found</p>
                <Button variant="primary" onClick={handleAddBeneficiary}>
                  <PlusCircle size={18} /> Add Your First Beneficiary
                </Button>
              </div>
            ) : (
              <div className="table-responsive">
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Name</th>
                      <th>Bank</th>
                      <th>Account Number</th>
                      <th>IFSC</th>
                      <th>Account Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {beneficiaryList.map((beneficiary, index) => (
                      <tr key={beneficiary.beneficiary_id}>
                        <td>{index + 1}</td>
                        <td>{beneficiary.beneficiary_name}</td>
                        <td>{beneficiary.bank_name}</td>
                        <td>{beneficiary.account_number}</td>
                        <td>{beneficiary.ifsc_code}</td>
                        <td>
                          {beneficiary.is_ac_validate === 1 ? (
                            <Badge bg="success">Verified</Badge>
                          ) : (
                            <Badge bg="warning">Not Verified</Badge>
                          )}
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => handleFundTransfer(beneficiary)}
                            >
                              <Send size={16} /> Transfer
                            </Button>
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => deleteBeneHandler(beneficiary)}
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* OTP Verification Modal */}
      <Modal show={showOtpModal} onHide={() => setShowOtpModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Verify OTP</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Enter OTP sent to your registered mobile number to delete
            beneficiary <strong>{selectedBeneficiary?.name}</strong>
          </p>
          <Form.Group className="mb-3">
            <Form.Label>OTP</Form.Label>
            <Form.Control
              type="text"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              placeholder="Enter OTP"
              maxLength={6}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowOtpModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={verifyDeleteBeneHandler}
            disabled={!otp.trim()}
          >
            Verify & Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default SenderHome;
